import React, { useCallback, useMemo, useRef, useState } from 'react';

import { globalObservability } from '@opendoor/observability/slim';

import { readClientProviderPreference } from '../clientProviderPreference';
import {
  getLastUsedWebAuthMethod,
  type LastUsedMethod,
  toLastUsedMethod,
} from '../lastUsedWebAuthMethod';
import {
  AccountFormContext,
  type AccountFormContextValue,
  type AccountFormStep,
  ENTRY_STEP,
} from './AccountFormContext';

// Where the method comes from is this package's business, not a consumer's: it
// is a cookie today and a cross-device lookup later, for the same decision.
const resolveLastUsed = (): LastUsedMethod | undefined => {
  const stored = getLastUsedWebAuthMethod();
  if (stored === undefined) {
    return undefined;
  }
  return toLastUsedMethod(stored);
};

export interface AccountFormProviderProps extends Pick<
  AccountFormContextValue,
  'onSuccess' | 'onError' | 'redirectTo'
> {
  children: React.ReactNode;
  /**
   * Seeds the shared `email` so the entry field opens on an address the caller
   * already knows — a link that carries one, or a resolved session. Read once,
   * at mount, so a value that arrives later needs `setEmail` instead.
   */
  initialEmail?: string;
  /**
   * Opens on a step other than entry, for a surface that has already done what
   * the entry screen does — reception's phone-number page knows the account and
   * only needs the registration screen's details. Read once, at mount, like
   * `initialEmail`; modules navigate with `setStep` from there.
   */
  initialStep?: AccountFormStep;
  /**
   * Opts this composition into ordering the provider buttons by the seller's
   * client — Google first in Chrome and on Android, Apple first in Safari and on
   * Apple hardware, and the declared order for any client we decline to classify.
   * `false` or omitted keeps the declared order for everyone.
   *
   * Lower precedence than the seller's own last-used method, which every form
   * resolves unconditionally: where that is known it wins, and this only decides
   * the order for sellers who have none. Both run at once, so an experiment on
   * this one is sized against the no-cookie population rather than all traffic —
   * a minority of returning sellers, most of whom carry a last-used method.
   *
   * Read once during the first render, for the reason the last-used method is.
   */
  clientOrderedProviders?: boolean;
}

/**
 * Owns the shared email/step state and provides it to whatever modules
 * render as children. Generic across whichever module set composes it —
 * `<AccountFormV2 />` is one such composition (this provider plus the
 * default module set), not the only one.
 */
export const AccountFormProvider: React.FC<AccountFormProviderProps> = ({
  onSuccess,
  onError,
  redirectTo,
  initialEmail,
  initialStep,
  clientOrderedProviders = false,
  children,
}) => {
  // Resolved during the first render rather than in an effect: an effect would
  // paint the default stack and then reorder live buttons under the user's
  // cursor. That holds only because no consumer server-renders this subtree —
  // cosmos keeps it behind a flag gate that shows a loader until the flag
  // resolves client-side, and reception is a client-only SPA. A server-rendered
  // mount would hydrate to a different order than it painted.
  const [lastUsedMethod] = useState(resolveLastUsed);
  const [clientPreferredProvider] = useState(() =>
    clientOrderedProviders ? readClientProviderPreference() : undefined,
  );
  const [email, setEmail] = useState(initialEmail ?? '');
  const [step, setStepState] = useState(initialStep ?? ENTRY_STEP);
  const [formError, setFormError] = useState<AccountFormContextValue['formError']>(null);

  // The alert region is screen-level state now, so leaving the screen has to
  // drop it: without this a failed lookup on one step would still be on show
  // after the next step replaced the module that reported it.
  const setStep = useCallback((next: AccountFormStep) => {
    setFormError(null);
    setStepState(next);
  }, []);

  const reportedBadges = useRef<Set<string>>(new Set());
  const reportBadgeImpression = useCallback((targetName: string): void => {
    if (reportedBadges.current.has(targetName)) {
      return;
    }
    try {
      // A component-view event, not an experiment impression: the account-form
      // slot's exposure is a separate emit, and pooling them would make badge
      // follow-through and arm assignment indistinguishable downstream.
      globalObservability.trackJourneyEvent('platform', 'textViewEvent', { targetName });
    } catch (error) {
      // `trackJourneyEvent` fans out to Journey, GTM, Datadog and Sentry and
      // guards none of it. Unguarded here, a blocked sink would throw out of the
      // effect that called this and unmount the form. Not claimed on failure, so
      // a transient sink does not silence this name for the rest of the session.
      reportBadgeTrackingError(error);
      return;
    }
    reportedBadges.current.add(targetName);
  }, []);

  const context = useMemo<AccountFormContextValue>(
    () => ({
      onSuccess,
      onError,
      redirectTo,
      email,
      setEmail,
      step,
      setStep,
      formError,
      setFormError,
      lastUsedMethod,
      clientPreferredProvider,
      reportBadgeImpression,
    }),
    [
      onSuccess,
      onError,
      redirectTo,
      email,
      step,
      setStep,
      formError,
      lastUsedMethod,
      clientPreferredProvider,
      reportBadgeImpression,
    ],
  );

  return <AccountFormContext.Provider value={context}>{children}</AccountFormContext.Provider>;
};

function reportBadgeTrackingError(error: unknown): void {
  try {
    globalObservability.trackError(
      'last-used-badge-view-journey-event-failed',
      error instanceof Error ? error : new Error(String(error)),
    );
  } catch {
    // The sink being unavailable is the case this exists for.
  }
}
